import { useState, useMemo, useEffect, useRef } from "react";
import { Button } from "@akuna/ui";
import { SearchField } from "@akuna/ui";
import { SegmentedTabs } from "@akuna/ui";
import { Plus, Edit, Trash2, ChevronLeft, ChevronRight, X, Filter, MoreVertical, ChevronDown, FileUp, Package, Wrench, AlertTriangle, History, TrendingUp, ShoppingCart, Boxes } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@akuna/ui";
import { LoadingSkeleton as Skeleton } from "@/components/ui/loading-skeleton";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { CatalogItemFormDialog } from "@/components/erp/CatalogItemFormDialog";
import { CatalogXmlImportDialog } from "@/components/erp/CatalogXmlImportDialog";
import { StockMovementDialog } from "@/components/erp/StockMovementDialog";
import { StockMovementsDialog } from "@/components/erp/StockMovementsDialog";
import { BarcodeScanner } from "@/components/ui/barcode-scanner";
import { PermissionGuard } from "@/components/PermissionGuard";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useDeleteItem, Item } from "@/hooks/useItems";
import { useProductSubtypes } from "@/hooks/useProductSubtypes";
import { usePackageSubtypes } from "@/hooks/usePackageSubtypes";
import { toast } from "sonner";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { saveLastErpView } from "@/lib/utils/erp-navigation";
import { useTranslation } from "react-i18next";
import { useMobileHeader } from "@/contexts/MobileHeaderContext";
import { useFiscalEnabled } from "@/hooks/useFiscalEnabled";
import { useIsMobile } from "@/hooks/use-mobile";
import { CatalogSectionNav } from "./catalog/CatalogSectionNav";
import { useCatalogStock, CatalogStockItem } from "@/hooks/useCatalogStock";
import { useModuleAccess } from "@/modules/useModuleAccess";

type TabKey = "all" | "products" | "services" | "kits" | "lowStock";

const LOW_STOCK_THRESHOLD = 5;
const PAGE_SIZE = 25;

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);

function calcMargin(sale: number, cost: number): number | null {
  if (cost <= 0) return null;
  return ((sale - cost) / cost) * 100;
}

function MarginBadge({ sale, cost }: { sale: number; cost: number }) {
  const margin = calcMargin(sale, cost);
  if (margin === null) return <span className="text-muted-foreground text-xs">—</span>;
  const cls = margin >= 100
    ? "text-emerald-600 font-semibold"
    : margin >= 40
      ? "text-amber-600 font-semibold"
      : "text-red-600 font-semibold";
  return <span className={cls}>{margin.toFixed(0)}%</span>;
}

function StockBar({ item, dense }: { item: CatalogStockItem; dense: boolean }) {
  if (!item.has_stock_entry) {
    return <span className="text-xs font-medium tabular-nums text-muted-foreground">0</span>;
  }
  const qty = item.stock_quantity;
  const barMax = Math.max(20, qty);
  const pct = Math.min(100, (qty / barMax) * 100);
  const isRupture = qty <= 0;
  const isLow = qty > 0 && qty <= LOW_STOCK_THRESHOLD;
  const barColor = isRupture ? "bg-red-500" : isLow ? "bg-amber-400" : "bg-emerald-500";
  return (
    <div className={cn("flex flex-col gap-1", dense ? "min-w-[80px]" : "min-w-[100px]")}>
      <div className="flex items-center justify-between gap-1">
        <span className={cn("text-xs font-medium tabular-nums", isRupture && "text-red-600", isLow && "text-amber-600")}>
          {qty}
        </span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
        <div className={cn("h-full rounded-full transition-all", barColor)} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function CatalogKpiCarousel({
  kpi,
  trackedCount,
  hasStock,
  t,
}: {
  kpi: { total: number; products: number; services: number; kits: number; stockValue: number; lowStock: number; servicesKits: number };
  trackedCount: number;
  hasStock: boolean;
  t: ReturnType<typeof useTranslation<"erp">>["t"];
}) {
  const items = [
    {
      label: t("catalogPage.unified.kpi.activeItems"),
      icon: Boxes,
      value: String(kpi.total),
      sub: t("catalogPage.unified.kpi.activeItemsSub", { products: kpi.products, services: kpi.services }),
    },
    ...(hasStock
      ? [
          {
            label: t("catalogPage.unified.kpi.stockValue"),
            icon: TrendingUp,
            value: formatCurrency(kpi.stockValue),
            sub: t("catalogPage.unified.kpi.stockValueSub", { count: trackedCount }),
          },
          {
            label: t("catalogPage.unified.kpi.lowStock"),
            icon: AlertTriangle,
            value: String(kpi.lowStock),
            sub: t("catalogPage.unified.kpi.lowStockSub"),
          },
        ]
      : []),
    {
      label: t("catalogPage.unified.kpi.servicesKits"),
      icon: ShoppingCart,
      value: String(kpi.servicesKits),
      sub: t("catalogPage.unified.kpi.servicesKitsSub"),
    },
  ];

  return (
    <div className="flex gap-3 overflow-x-auto pb-1 snap-x snap-mandatory md:hidden [-webkit-overflow-scrolling:touch]">
      {items.map(({ label, icon: Icon, value, sub }) => (
        <Card key={label} className="min-w-[160px] shrink-0 snap-start shadow-sm bg-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-muted-foreground">{label}</span>
              <Icon className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-lg font-bold text-foreground truncate">{value}</p>
            <p className="text-xs text-muted-foreground mt-1 truncate">{sub}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function CatalogMobileFilterSheet({
  open,
  onOpenChange,
  showSubtypeFilter,
  subtypeSource,
  selectedSubtypeIds,
  toggleSubtype,
  onClearSubtypes,
  onImportXml,
  t,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  showSubtypeFilter: boolean;
  subtypeSource: { id: string; name: string }[];
  selectedSubtypeIds: string[];
  toggleSubtype: (id: string) => void;
  onClearSubtypes: () => void;
  onImportXml: () => void;
  t: ReturnType<typeof useTranslation<"erp">>["t"];
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-2xl">
        <SheetHeader>
          <SheetTitle>{t("catalogPage.unified.mobile.filtersButton")}</SheetTitle>
        </SheetHeader>
        <div className="mt-4 flex flex-col gap-4 pb-[env(safe-area-inset-bottom)]">
          <Button
            variant="outline"
            className="h-11 justify-start gap-2 rounded-full"
            onClick={() => {
              onOpenChange(false);
              onImportXml();
            }}
          >
            <FileUp className="h-4 w-4" />
            {t("catalogPage.unified.mobile.importXml")}
          </Button>

          {showSubtypeFilter && (
            <div className="space-y-3">
              <div className="font-medium text-sm">{t("catalogPage.filterBySubtype")}</div>
              <div className="space-y-2">
                {subtypeSource.map((st) => (
                  <div key={st.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`mobile-st-${st.id}`}
                      checked={selectedSubtypeIds.includes(st.id)}
                      onCheckedChange={() => toggleSubtype(st.id)}
                    />
                    <Label htmlFor={`mobile-st-${st.id}`} className="text-sm font-normal cursor-pointer flex-1">
                      {st.name.charAt(0).toUpperCase() + st.name.slice(1)}
                    </Label>
                  </div>
                ))}
              </div>
              {selectedSubtypeIds.length > 0 && (
                <Button variant="ghost" size="sm" onClick={onClearSubtypes} className="w-full gap-2">
                  <X className="h-4 w-4" />
                  {t("common:clearSearch")}
                </Button>
              )}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function CatalogMobileCardsLoading({ count = 4 }: { count?: number }) {
  return (
    <div className="space-y-3 md:hidden">
      {Array.from({ length: count }).map((_, index) => (
        <Card key={`catalog-mobile-skeleton-${index}`} className="rounded-2xl border-border/60 shadow-sm">
          <CardContent className="space-y-3 p-4">
            <div className="flex items-center gap-3">
              <Skeleton className="h-9 w-9 rounded-md" />
              <div className="min-w-0 flex-1 space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-20" />
              </div>
            </div>
            <div className="border-t pt-3 space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-3 w-full" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function CatalogMobileCard({
  item,
  onTap,
  hasStock,
  hasFinancial,
  t,
}: {
  item: CatalogStockItem;
  onTap: (item: CatalogStockItem) => void;
  hasStock: boolean;
  hasFinancial: boolean;
  t: ReturnType<typeof useTranslation<"erp">>["t"];
}) {
  const subtypeName = item.product_subtype?.name ?? item.package_subtype?.name ?? null;
  const showFooter = hasStock || hasFinancial;

  return (
    <Card
      className="overflow-hidden rounded-2xl border-border/60 bg-card shadow-sm md:hidden"
      onClick={() => onTap(item)}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={cn(
            "flex h-9 w-9 shrink-0 items-center justify-center rounded-md",
            item.type === "PRODUTO" ? "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400"
            : item.type === "SERVICO" ? "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400"
            : "bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400"
          )}>
            {item.type === "SERVICO" ? <Wrench className="h-4 w-4" /> : <Package className="h-4 w-4" />}
          </div>

          <div className="min-w-0 flex-1">
            <p className="truncate font-semibold text-foreground">{item.name}</p>
            <div className="mt-0.5 flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
              {item.sku && <span className="font-mono truncate">{item.sku}</span>}
              {subtypeName && (
                <>
                  <span className="h-1 w-1 shrink-0 rounded-full bg-border" />
                  <span className="truncate">{subtypeName.charAt(0).toUpperCase() + subtypeName.slice(1)}</span>
                </>
              )}
            </div>
          </div>

          {hasFinancial && (
            <div className="shrink-0 text-right">
              <p className="font-semibold text-sm">{formatCurrency(item.sale_price)}</p>
              {item.cost_price > 0 && (
                <p className="text-xs text-muted-foreground">
                  {t("catalogPage.unified.mobile.cost", { value: formatCurrency(item.cost_price) })}
                </p>
              )}
            </div>
          )}
        </div>

        {showFooter && (
          <div className={cn("mt-3 flex items-center border-t border-border/70 pt-3", hasStock ? "justify-between" : "justify-end")}>
            {hasStock && (
              <div onClick={(e) => e.stopPropagation()}>
                <StockBar item={item} dense />
              </div>
            )}
            {hasFinancial && <MarginBadge sale={item.sale_price} cost={item.cost_price} />}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CatalogMobileActionsSheet({
  item,
  onOpenChange,
  onEdit,
  onStockMovement,
  onMovements,
  onDelete,
  hasStock,
  t,
}: {
  item: CatalogStockItem | null;
  onOpenChange: (open: boolean) => void;
  onEdit: (item: Item) => void;
  onStockMovement: (item: CatalogStockItem) => void;
  onMovements: (item: CatalogStockItem) => void;
  onDelete: (item: CatalogStockItem) => void;
  hasStock: boolean;
  t: ReturnType<typeof useTranslation<"erp">>["t"];
}) {
  return (
    <Sheet open={!!item} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-2xl">
        <SheetHeader>
          <SheetTitle>{item?.name ?? t("catalogPage.unified.mobile.actionsSheetTitle")}</SheetTitle>
        </SheetHeader>
        <div className="mt-4 flex flex-col gap-2 pb-[env(safe-area-inset-bottom)]">
          <Button
            variant="outline"
            className="h-12 justify-start gap-2 rounded-full px-5 text-[15px] font-bold"
            onClick={() => { if (item) onEdit(item); onOpenChange(false); }}
          >
            <Edit className="h-[18px] w-[18px]" />
            {t("common.edit")}
          </Button>
          {hasStock && item?.type === "PRODUTO" && (
            <>
              <Button
                variant="outline"
                className="h-12 justify-start gap-2 rounded-full px-5 text-[15px] font-bold"
                onClick={() => { if (item) onStockMovement(item); onOpenChange(false); }}
              >
                <Boxes className="h-[18px] w-[18px]" />
                {t("catalogPage.unified.mobile.adjustStock")}
              </Button>
              <Button
                variant="outline"
                className="h-12 justify-start gap-2 rounded-full px-5 text-[15px] font-bold"
                onClick={() => { if (item) onMovements(item); onOpenChange(false); }}
              >
                <History className="h-[18px] w-[18px]" />
                {t("catalogPage.unified.mobile.viewMovements")}
              </Button>
            </>
          )}
          <Button
            variant="outline"
            className="h-12 justify-start gap-2 rounded-full border-destructive/40 px-5 text-[15px] font-bold text-destructive"
            onClick={() => { if (item) onDelete(item); onOpenChange(false); }}
          >
            <Trash2 className="h-[18px] w-[18px]" />
            {t("common:delete")}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default function Catalog() {
  const { t } = useTranslation("erp");
  const fiscalEnabled = useFiscalEnabled();
  const isMobile = useIsMobile();
  const { has } = useModuleAccess();
  const hasStock = has("stock");
  const hasFinancial = has("financial");

  // Filters
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState<TabKey>("all");
  const [selectedSubtypeIds, setSelectedSubtypeIds] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [mobileVisibleCount, setMobileVisibleCount] = useState(PAGE_SIZE);
  const mobileLoadMoreRef = useRef<HTMLDivElement | null>(null);

  // Dialogs
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isXmlImportOpen, setIsXmlImportOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Item | undefined>();
  const [itemToDelete, setItemToDelete] = useState<CatalogStockItem | null>(null);
  const [stockMovementItem, setStockMovementItem] = useState<{ id: string; name: string } | null>(null);
  const [movementsItem, setMovementsItem] = useState<{ id: string; name: string } | null>(null);
  const [mobileActionsItem, setMobileActionsItem] = useState<CatalogStockItem | null>(null);

  const { data: productSubtypes = [] } = useProductSubtypes();
  const { data: packageSubtypes = [] } = usePackageSubtypes();
  const deleteItem = useDeleteItem();

  // Fetch ALL items + stock (no pagination — filtered client-side)
  const { items: allItems, isLoading } = useCatalogStock({ search });

  useEffect(() => { saveLastErpView("catalog"); }, []);

  // KPI calculations (whole unfiltered set)
  const kpi = useMemo(() => {
    const products = allItems.filter((i) => i.type === "PRODUTO");
    const services = allItems.filter((i) => i.type === "SERVICO");
    const kits = allItems.filter((i) => i.type === "PACOTE");
    const stockValue = allItems.reduce((sum, i) =>
      i.has_stock_entry ? sum + i.cost_price * i.stock_quantity : sum, 0
    );
    const lowStock = allItems.filter(
      (i) => i.has_stock_entry && i.stock_quantity > 0 && i.stock_quantity <= LOW_STOCK_THRESHOLD
    );
    const servicesKits = [...services, ...kits];
    return {
      total: allItems.length,
      products: products.length,
      services: services.length,
      kits: kits.length,
      stockValue,
      lowStock: lowStock.length,
      servicesKits: servicesKits.length,
    };
  }, [allItems]);

  // Subtype-filtered + tab-filtered items
  const tabFiltered = useMemo(() => {
    let base = allItems;

    // Tab filtering
    if (activeTab === "products") base = base.filter((i) => i.type === "PRODUTO");
    else if (activeTab === "services") base = base.filter((i) => i.type === "SERVICO");
    else if (activeTab === "kits") base = base.filter((i) => i.type === "PACOTE");
    else if (activeTab === "lowStock")
      base = base.filter((i) => i.has_stock_entry && i.stock_quantity > 0 && i.stock_quantity <= LOW_STOCK_THRESHOLD);

    // Multi-subtype filter (client-side)
    if (selectedSubtypeIds.length > 0) {
      base = base.filter((i) =>
        activeTab === "kits"
          ? Boolean(i.package_subtype_id && selectedSubtypeIds.includes(i.package_subtype_id))
          : Boolean(i.product_subtype_id && selectedSubtypeIds.includes(i.product_subtype_id))
      );
    }

    return [...base].sort((a, b) =>
      a.name.localeCompare(b.name, "pt-BR", { sensitivity: "base" }) ||
      (a.sku ?? "").localeCompare(b.sku ?? "", "pt-BR", { sensitivity: "base" })
    );
  }, [allItems, activeTab, selectedSubtypeIds]);

  // Paginated display
  const totalPages = Math.ceil(tabFiltered.length / PAGE_SIZE);
  const displayItems = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return tabFiltered.slice(start, start + PAGE_SIZE);
  }, [tabFiltered, currentPage]);

  const mobileDisplayItems = useMemo(
    () => tabFiltered.slice(0, mobileVisibleCount),
    [tabFiltered, mobileVisibleCount],
  );
  const hasMoreMobileItems = mobileVisibleCount < tabFiltered.length;

  useEffect(() => {
    if (!isMobile || !hasMoreMobileItems) return;

    const node = mobileLoadMoreRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setMobileVisibleCount((count) => Math.min(count + PAGE_SIZE, tabFiltered.length));
        }
      },
      { rootMargin: "240px 0px" },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMoreMobileItems, isMobile, tabFiltered.length]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setCurrentPage(1);
    setMobileVisibleCount(PAGE_SIZE);
  };

  const toggleSubtype = (id: string) => {
    setSelectedSubtypeIds((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
    setCurrentPage(1);
    setMobileVisibleCount(PAGE_SIZE);
  };

  const handleTabChange = (tab: TabKey) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setSelectedSubtypeIds([]);
    setMobileVisibleCount(PAGE_SIZE);
  };

  const handleEdit = (item: Item) => {
    setSelectedItem(item);
    setIsFormOpen(true);
  };

  const handleDelete = (item: CatalogStockItem) => setItemToDelete(item);

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    try {
      await deleteItem.mutateAsync(itemToDelete.id);
      setItemToDelete(null);
    } catch (error) {
      const msg = error instanceof Error ? error.message : t("errors.unknown", { ns: "errors", defaultValue: "Unknown error" });
      toast.error(t("catalogPage.deleteError", { message: msg }));
      setItemToDelete(null);
    }
  };

  const handleNewItem = () => {
    setSelectedItem(undefined);
    setIsFormOpen(true);
  };

  // Subtype list for the popover (depends on active tab)
  const subtypeSource =
    activeTab === "kits" ? packageSubtypes
    : activeTab === "products" || activeTab === "all" ? productSubtypes
    : [];
  const showSubtypeFilter = ["all", "products", "kits"].includes(activeTab) && subtypeSource.length > 0;

  useMobileHeader({
    title: t("catalogPage.unified.title"),
    search: (
      <div className="flex items-center gap-2">
        <SearchField
          className="flex-1 min-w-0"
          label={t("catalogPage.searchPlaceholder")}
          clearLabel={t("common:clearSearch")}
          placeholder={t("catalogPage.searchPlaceholder")}
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          onClear={() => handleSearchChange("")}
        />
        <div className="shrink-0"><BarcodeScanner onScan={handleSearchChange} buttonSize="default" /></div>
        <Button
          variant="outline"
          size="icon"
          className={cn("shrink-0", selectedSubtypeIds.length > 0 && "border-primary bg-primary/5")}
          onClick={() => setIsMobileFilterOpen(true)}
        >
          <Filter className="h-4 w-4" />
          {selectedSubtypeIds.length > 0 && (
            <span className="sr-only">{t("catalogPage.unified.mobile.filtersButton")} ({selectedSubtypeIds.length})</span>
          )}
        </Button>
      </div>
    ),
  }, [t, search, selectedSubtypeIds]);

  const tabs: { key: TabKey; label: string }[] = [
    { key: "all", label: t("catalogPage.unified.tabs.all") },
    { key: "products", label: t("catalogPage.unified.tabs.products") },
    { key: "services", label: t("catalogPage.unified.tabs.services") },
    { key: "kits", label: t("catalogPage.unified.tabs.kits") },
    ...(hasStock ? [{ key: "lowStock" as const, label: t("catalogPage.unified.tabs.lowStock") }] : []),
  ];

  return (
    <PermissionGuard resource="catalog" action="view">
      <div className="space-y-5">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
          <div className="hidden md:block">
            <h1 className="text-2xl font-bold text-foreground">{t("catalogPage.unified.title")}</h1>
          </div>
          <div className="hidden md:flex gap-2 shrink-0">
            <Button variant="outline" onClick={() => setIsXmlImportOpen(true)}>
              <FileUp className="h-4 w-4 mr-2" />
              {t("catalogPage.xmlButton")}
            </Button>
            <Button onClick={handleNewItem}>
              <Plus className="h-4 w-4 mr-2" />
              {t("common.add")} {t("common.item").toLowerCase()}
            </Button>
          </div>
        </div>

        <CatalogSectionNav active="items" />

        {/* KPI Row (desktop) */}
        <div className={cn("hidden md:grid grid-cols-2 gap-3", hasStock ? "sm:grid-cols-4" : "sm:grid-cols-2")}>
          <Card className="shadow-sm bg-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-muted-foreground">{t("catalogPage.unified.kpi.activeItems")}</span>
                <Boxes className="h-4 w-4 text-muted-foreground" />
              </div>
              <p className="text-2xl font-bold text-foreground">{kpi.total}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {t("catalogPage.unified.kpi.activeItemsSub", { products: kpi.products, services: kpi.services })}
              </p>
            </CardContent>
          </Card>

          {hasStock && (
            <>
              <Card className="shadow-sm bg-card">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-muted-foreground">{t("catalogPage.unified.kpi.stockValue")}</span>
                    <TrendingUp className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <p className="text-lg font-bold text-foreground truncate">{formatCurrency(kpi.stockValue)}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t("catalogPage.unified.kpi.stockValueSub", { count: allItems.filter((i) => i.has_stock_entry).length })}
                  </p>
                </CardContent>
              </Card>

              <Card className="shadow-sm bg-card">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-muted-foreground">{t("catalogPage.unified.kpi.lowStock")}</span>
                    <AlertTriangle className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <p className="text-2xl font-bold text-foreground">{kpi.lowStock}</p>
                  <p className="text-xs text-muted-foreground mt-1">{t("catalogPage.unified.kpi.lowStockSub")}</p>
                </CardContent>
              </Card>
            </>
          )}

          <Card className="shadow-sm bg-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-muted-foreground">{t("catalogPage.unified.kpi.servicesKits")}</span>
                <ShoppingCart className="h-4 w-4 text-muted-foreground" />
              </div>
              <p className="text-2xl font-bold text-foreground">{kpi.servicesKits}</p>
              <p className="text-xs text-muted-foreground mt-1">{t("catalogPage.unified.kpi.servicesKitsSub")}</p>
            </CardContent>
          </Card>
        </div>

        <CatalogKpiCarousel
          kpi={kpi}
          trackedCount={allItems.filter((i) => i.has_stock_entry).length}
          hasStock={hasStock}
          t={t}
        />

        {/* Toolbar */}
        <div className="flex flex-col gap-3">
          {/* Segmented tabs */}
          <div className="overflow-x-auto">
            <SegmentedTabs
              value={activeTab}
              variant="outline"
              onValueChange={(value) => handleTabChange(value as TabKey)}
              items={tabs.map(({ key, label }) => ({
                value: key,
                label: (
                  <>
                    {label}
                    {key === "lowStock" && kpi.lowStock > 0 && (
                      <span className={cn(
                        "ml-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-xs font-bold",
                        activeTab === key ? "bg-primary-foreground/20 text-primary-foreground" : "bg-amber-500 text-white",
                      )}>
                        {kpi.lowStock}
                      </span>
                    )}
                  </>
                ),
              }))}
            />
          </div>

          {/* Search + subtype filter (desktop; mobile uses the header search slot) */}
          <div className="hidden md:flex md:flex-row gap-2 items-start sm:items-center">
            <SearchField
              className="flex-1 min-w-0"
              label={t("catalogPage.searchPlaceholder")}
              clearLabel={t("common:clearSearch")}
              placeholder={t("catalogPage.searchPlaceholder")}
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              onClear={() => handleSearchChange("")}
            />
            <BarcodeScanner onScan={handleSearchChange} buttonSize="default" />

            {showSubtypeFilter && (
              <Popover>
                {/* desktop-only trigger; mobile uses CatalogMobileFilterSheet below */}
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "hidden md:inline-flex gap-2",
                      selectedSubtypeIds.length > 0 && "border-primary bg-primary/5"
                    )}
                  >
                    <Filter className="h-4 w-4 opacity-50" />
                    <span className="hidden sm:inline">{t("catalogPage.subtypeLabel")}</span>
                    {selectedSubtypeIds.length > 0 && (
                      <Badge variant="secondary" className="h-4 min-w-4 px-1 rounded-full text-xs">
                        {selectedSubtypeIds.length}
                      </Badge>
                    )}
                    <ChevronDown className="h-4 w-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-4" align="start">
                  <div className="space-y-3">
                    <div className="font-medium text-sm">{t("catalogPage.filterBySubtype")}</div>
                    <div className="space-y-2">
                      {subtypeSource.map((st) => (
                        <div key={st.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`st-${st.id}`}
                            checked={selectedSubtypeIds.includes(st.id)}
                            onCheckedChange={() => toggleSubtype(st.id)}
                          />
                          <Label htmlFor={`st-${st.id}`} className="text-sm font-normal cursor-pointer flex-1">
                            {st.name.charAt(0).toUpperCase() + st.name.slice(1)}
                          </Label>
                        </div>
                      ))}
                    </div>
                    {selectedSubtypeIds.length > 0 && (
                      <Button variant="ghost" size="sm" onClick={() => setSelectedSubtypeIds([])} className="w-full gap-2">
                        <X className="h-4 w-4" />
                        {t("common:clearSearch")}
                      </Button>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            )}

          </div>
        </div>

        <CatalogMobileFilterSheet
          open={isMobileFilterOpen}
          onOpenChange={setIsMobileFilterOpen}
          showSubtypeFilter={showSubtypeFilter}
          subtypeSource={subtypeSource}
          selectedSubtypeIds={selectedSubtypeIds}
          toggleSubtype={toggleSubtype}
          onClearSubtypes={() => setSelectedSubtypeIds([])}
          onImportXml={() => setIsXmlImportOpen(true)}
          t={t}
        />

        {/* Content */}
        {isLoading ? (
          <>
            <div className="hidden items-center justify-center py-16 md:flex">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              <span className="ml-3 text-muted-foreground text-sm">{t("catalogPage.loading")}</span>
            </div>
            <CatalogMobileCardsLoading />
          </>
        ) : tabFiltered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Package className="h-12 w-12 text-muted-foreground mb-4 opacity-40" />
            <h3 className="text-base font-semibold mb-1">{t("catalogPage.unified.emptySearch")}</h3>
            <p className="text-sm text-muted-foreground">{t("catalogPage.unified.emptySearchHint")}</p>
          </div>
        ) : (
          <>
            <div className="hidden md:block">
              <TableView
                items={displayItems}
                dense={false}
                fiscalEnabled={fiscalEnabled}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onStockMovement={(item) => setStockMovementItem({ id: item.id, name: item.name })}
                onMovements={(item) => setMovementsItem({ id: item.id, name: item.name })}
                hasStock={hasStock}
                hasFinancial={hasFinancial}
                t={t}
              />
            </div>

            <div className="space-y-3 pb-24 md:hidden">
              {mobileDisplayItems.map((item) => (
                <CatalogMobileCard
                  key={item.id}
                  item={item}
                  onTap={setMobileActionsItem}
                  hasStock={hasStock}
                  hasFinancial={hasFinancial}
                  t={t}
                />
              ))}
              <div ref={mobileLoadMoreRef} className="py-3 text-center text-xs text-muted-foreground">
                {hasMoreMobileItems
                  ? <span className="sr-only">{t("catalogPage.unified.mobile.loadingMore")}</span>
                  : <span>{t("catalogPage.unified.mobile.endOfResults")}</span>}
              </div>
            </div>

            <CatalogMobileActionsSheet
              item={mobileActionsItem}
              onOpenChange={(open) => !open && setMobileActionsItem(null)}
              onEdit={handleEdit}
              onStockMovement={(item) => setStockMovementItem({ id: item.id, name: item.name })}
              onMovements={(item) => setMovementsItem({ id: item.id, name: item.name })}
              onDelete={handleDelete}
              hasStock={hasStock}
              t={t}
            />
          </>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-col md:flex-row gap-2 items-center justify-between px-4 py-3 border rounded-lg bg-muted/30">
            <span className="text-sm text-muted-foreground">
              {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, tabFiltered.length)} / {tabFiltered.length}
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="outline" size="sm"
                onClick={() => { setCurrentPage((p) => Math.max(1, p - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
                {t("common:pagination.previous")}
              </Button>
              {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                const page = totalPages <= 7 ? i + 1 : i + Math.max(1, currentPage - 3);
                if (page > totalPages) return null;
                return (
                  <Button
                    key={page}
                    variant={currentPage === page ? "default" : "outline"}
                    size="sm"
                    className="w-8 h-8 p-0"
                    onClick={() => { setCurrentPage(page); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                  >
                    {page}
                  </Button>
                );
              })}
              <Button
                variant="outline" size="sm"
                onClick={() => { setCurrentPage((p) => Math.min(totalPages, p + 1)); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                disabled={currentPage >= totalPages}
              >
                {t("common:pagination.next")}
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        <div className="fixed bottom-[calc(96px+env(safe-area-inset-bottom))] right-4 z-40 md:hidden">
          <Button
            className="h-12 w-12 rounded-full p-0 shadow-xl shadow-primary/25"
            onClick={handleNewItem}
          >
            <Plus className="h-5 w-5" />
            <span className="sr-only">{t("common.add")} {t("common.item").toLowerCase()}</span>
          </Button>
        </div>

        {/* Dialogs */}
        <CatalogItemFormDialog
          open={isFormOpen}
          onOpenChange={setIsFormOpen}
          item={selectedItem}
        />
        <CatalogXmlImportDialog
          open={isXmlImportOpen}
          onOpenChange={setIsXmlImportOpen}
          onImportComplete={() => setIsXmlImportOpen(false)}
        />
        <StockMovementsDialog
          open={!!movementsItem}
          onOpenChange={(open) => !open && setMovementsItem(null)}
          itemId={movementsItem?.id ?? null}
          itemName={movementsItem?.name ?? null}
        />
        <StockMovementDialog
          open={!!stockMovementItem}
          onOpenChange={(open) => !open && setStockMovementItem(null)}
          itemId={stockMovementItem?.id ?? null}
          itemName={stockMovementItem?.name ?? null}
        />
        <ConfirmationDialog
          open={!!itemToDelete}
          onOpenChange={(open) => !open && setItemToDelete(null)}
          title={t("common:delete")}
          description={
            itemToDelete?.type === "PRODUTO"
              ? t("catalogPage.deleteConfirmProduct", { name: itemToDelete?.name ?? "" })
              : t("catalogPage.deleteConfirmDefault", { name: itemToDelete?.name ?? "" })
          }
          confirmText={t("common:delete")}
          cancelText={t("common:cancel")}
          variant="destructive"
          onConfirm={handleConfirmDelete}
        />
      </div>
    </PermissionGuard>
  );
}

// ─── Table View ─────────────────────────────────────────────────────────────

interface TableViewProps {
  items: CatalogStockItem[];
  dense: boolean;
  fiscalEnabled: boolean;
  onEdit: (item: Item) => void;
  onDelete: (item: CatalogStockItem) => void;
  onStockMovement: (item: CatalogStockItem) => void;
  onMovements: (item: CatalogStockItem) => void;
  hasStock: boolean;
  hasFinancial: boolean;
  t: ReturnType<typeof useTranslation<"erp">>["t"];
}

function TableView({ items, dense, fiscalEnabled, onEdit, onDelete, onStockMovement, onMovements, hasStock, hasFinancial, t }: TableViewProps) {
  const rowPy = dense ? "py-1.5" : "py-3";
  return (
    <div className="rounded-lg border overflow-hidden bg-background">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/40">
            <TableHead className="w-[280px]">{t("catalogPage.unified.columns.item")}</TableHead>
            <TableHead className="hidden md:table-cell">{t("catalogPage.unified.columns.subtype")}</TableHead>
            {hasFinancial && <TableHead className="text-right hidden sm:table-cell">{t("catalogPage.unified.columns.price")}</TableHead>}
            {hasFinancial && <TableHead className="text-right hidden lg:table-cell">{t("catalogPage.unified.columns.margin")}</TableHead>}
            {hasStock && <TableHead className="hidden sm:table-cell">{t("catalogPage.unified.columns.stock")}</TableHead>}
            <TableHead className="w-10" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item, index) => (
            <TableRow
              key={item.id}
              className={cn(
                "cursor-pointer transition-colors hover:bg-muted/40",
                index % 2 === 0 ? "bg-background" : "bg-muted/25",
              )}
              onClick={() => onEdit(item)}
            >
              {/* Item */}
              <TableCell className={rowPy}>
                <div className="flex items-center gap-2.5">
                  <div className={cn(
                    "flex items-center justify-center rounded-md shrink-0",
                    dense ? "h-7 w-7" : "h-9 w-9",
                    item.type === "PRODUTO" ? "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400"
                    : item.type === "SERVICO" ? "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400"
                    : "bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400"
                  )}>
                    {item.type === "SERVICO"
                      ? <Wrench className={cn(dense ? "h-3.5 w-3.5" : "h-4 w-4")} />
                      : <Package className={cn(dense ? "h-3.5 w-3.5" : "h-4 w-4")} />}
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-sm leading-tight truncate">{item.name}</p>
                    {item.sku && (
                      <p className="font-mono text-xs text-muted-foreground truncate">{item.sku}</p>
                    )}
                  </div>
                </div>
              </TableCell>

              {/* Subtype */}
              <TableCell className={cn(rowPy, "hidden md:table-cell text-sm text-muted-foreground")}>
                {item.product_subtype?.name
                  ? item.product_subtype.name.charAt(0).toUpperCase() + item.product_subtype.name.slice(1)
                  : item.package_subtype?.name
                    ? item.package_subtype.name.charAt(0).toUpperCase() + item.package_subtype.name.slice(1)
                    : "—"}
              </TableCell>

              {/* Price */}
              {hasFinancial && (
                <TableCell className={cn(rowPy, "text-right hidden sm:table-cell")}>
                  <p className="font-semibold text-sm">{formatCurrency(item.sale_price)}</p>
                  {item.cost_price > 0 && (
                    <p className="text-xs text-muted-foreground">{formatCurrency(item.cost_price)}</p>
                  )}
                </TableCell>
              )}

              {/* Margin */}
              {hasFinancial && (
                <TableCell className={cn(rowPy, "text-right hidden lg:table-cell")}>
                  <MarginBadge sale={item.sale_price} cost={item.cost_price} />
                </TableCell>
              )}

              {/* Stock */}
              {hasStock && (
                <TableCell className={cn(rowPy, "hidden sm:table-cell")} onClick={(e) => e.stopPropagation()}>
                  <StockBar item={item} dense={dense} />
                </TableCell>
              )}

              {/* Actions */}
              <TableCell className={cn(rowPy, "text-right")} onClick={(e) => e.stopPropagation()}>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreVertical className="h-4 w-4" />
                      <span className="sr-only">{t("catalogPage.openMenu")}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuLabel>{t("common.actions")}</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => onEdit(item)}>
                      <Edit className="mr-2 h-4 w-4" />
                      {t("common.edit")}
                    </DropdownMenuItem>
                    {hasStock && item.type === "PRODUTO" && (
                      <>
                        <DropdownMenuItem onClick={() => onStockMovement(item)}>
                          <Boxes className="mr-2 h-4 w-4" />
                          {t("itemForm.stock.adjust")}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onMovements(item)}>
                          <History className="mr-2 h-4 w-4" />
                          {t("stock.movements", { defaultValue: "Movimentações" })}
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => onDelete(item)}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      {t("common:delete")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
